import { AbilityContext, Can } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import { Receive } from "@/types/general";
import { ReactComponent as DetailsSVG } from "@assets/icons/ic_document_search.svg";
import { useAbility } from "@casl/react";
import {
    genT,
    PredefinedTranslations,
    removeEmpty,
    useDatesRangeFilter,
    useOrdering,
    usePagination,
} from "@helpers/utils";
import { useGetModuleEmployeesQuery } from "@redux/features/employees/employeesApi";
import { useGetReceiptsFullQuery } from "@redux/features/receipts/receiptsApi";
import { useGetModuleSuppliersQuery } from "@redux/features/suppliers/suppliersApi";
import BaseAdminFilter from "@reusables/BaseAdminFilter";
import BaseDatepicker from "@reusables/BaseDatepickerLegacy";
import { DatepickerRangeContext } from "@reusables/BaseDatepickerLegacy/context";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseTable from "@reusables/BaseTable";
import BaseTablePaginationControls from "@reusables/BaseTablePaginationControls";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseDropdownLegacy from "@reusables/dropdowns/BaseDropdownLegacy";
import _ from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import DeletionModal from "../components/modals/DeletionModal";
import { Paperclip } from "lucide-react";

function hasReceiptDocuments(receipt: Receive.Extended) {
    return (
        !!receipt.document_number ||
        receipt.has_attachments ||
        (receipt.attachments?.length ?? 0) > 0
    );
}

function getReceiptAttachmentsCount(receipt: Receive.Extended) {
    return receipt.attachments?.length ?? 0;
}

export default function ReceiveMainPage() {
    const history = useHistory();
    const { t } = useTranslation("", { keyPrefix: "receive.main" });

    // Searching value with debounce
    const [searchingValue, setSearchingValue] = useState<string>();
    const searchInputDebounce = useCallback(_.debounce(setSearchingValue, 1000), [
        setSearchingValue,
    ]);

    // Loading suppliers for the dropdown
    const { data: suppliersOptions, isLoading: suppliersLoading } =
        useGetModuleSuppliersQuery("receive");
    const [selectedSuppliers, setSelectedSuppliers] = useState<
        NonNullable<typeof suppliersOptions>
    >([]);

    // Loading employees for the "Our reference" dropdown
    const { data: ourReferenceOptions, isLoading: ourReferenceOptionsLoading } =
        useGetModuleEmployeesQuery("receive");
    const [selectedOurReferences, setSelectedOurReferences] = useState<
        NonNullable<typeof ourReferenceOptions>
    >([]);

    const { datesRange, setDatesRange, filterAdaptedDatesRange } = useDatesRangeFilter();

    // Table ordering
    const { orderBy, setOrderBy } = useOrdering<Receive.DTO.OrderBy>({ name: "id", type: "desc" });

    const pagination = usePagination({ storageKey: "receive.main" });

    const filters = removeEmpty({
        search: searchingValue,
        suppliers: selectedSuppliers.map((x) => x.id),
        our_references: selectedOurReferences.map((x) => x.id),
        dates_range: filterAdaptedDatesRange,
    });

    const { data: receiptsResponse, isFetching: areReceiptsLoading } = useGetReceiptsFullQuery({
        filters,
        orderBy,
        pagination: pagination.request,
    });
    const showDocumentColumns = receiptsResponse?.payload.some(hasReceiptDocuments) ?? false;

    const [deletionReceivementId, setDeletionReceivementId] = useState<number>();

    const collapsedContent = (rowRelated: Receive.Extended) => {
        const combinedItems = rowRelated.receives.flatMap((receive) => {
            if (receive.type === "line") {
                // If it's a line, return it directly
                return [receive];
            } else if (receive.type === "group") {
                return receive.data.lines;
            }
            return [];
        });

        return (
            <BaseTable
                data={combinedItems}
                columns={[
                    {
                        header: t("subtable.columns.productId"),
                        getter: (row) => row.data?.order.product.code ?? "-",
                    },
                    {
                        header: t("subtable.columns.product"),
                        getter: (row) => row.data?.order.product.name ?? "-",
                    },
                    {
                        header: t("subtable.columns.location"),
                        getter: (row) => row.data?.location?.store.name ?? "-",
                    },
                    {
                        header: t("subtable.columns.section"),
                        getter: (row) => row.data?.location?.section?.name ?? "-",
                    },
                    {
                        header: t("subtable.columns.quantity"),
                        getter: (row) => (
                            <div>
                                {row.data?.received_quantity}
                                {/*<span*/}
                                {/*    className="text-accent font-[500] ml-[3px]">{generalT("of_part")} {subRow.available_quantity}</span>*/}
                            </div>
                        ),
                    },
                ]}
                size="small"
                boldHeaders
                nothingFound={{
                    height: 200,
                }}
                hideTableWhenNothingFound
            />
        );
    };

    return (
        <>
            {/* FILTERS BLOCK */}
            <BaseAdminFilter
                permissionModule="receive"
                filterItems={
                    <div className="w-[702px]">
                        <BaseInputsGrid cols={2} gap={24}>
                            <BaseDropdownLegacy
                                label={t("filters.supplier.label")}
                                brightLabel
                                options={suppliersOptions}
                                getter={{
                                    label: (opt) => opt.name,
                                    key: (opt) => opt.id,
                                }}
                                value={selectedSuppliers}
                                onChange={setSelectedSuppliers}
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                isLoading={suppliersLoading}
                                autocomplete
                                multiple
                            />

                            <BaseDropdown
                                label={t("filters.ourReference.label")}
                                brightLabel
                                options={ourReferenceOptions}
                                getter={{
                                    label: (opt) => opt.name,
                                    key: (opt) => opt.id,
                                }}
                                value={selectedOurReferences}
                                onChange={(_, val) => setSelectedOurReferences(val)}
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                isLoading={ourReferenceOptionsLoading}
                                autocomplete
                                multiple
                            />

                            <DatepickerRangeContext.Provider
                                value={{
                                    dates_range: { range: datesRange, setRange: setDatesRange },
                                }}
                            >
                                <BaseDatepicker
                                    label={t("filters.date.label")}
                                    placeholder={t("filters.date.placeholder")}
                                    rangeConfig={{ groupKey: "dates_range", role: "solo" }}
                                    brightLabel
                                />
                            </DatepickerRangeContext.Provider>
                        </BaseInputsGrid>
                    </div>
                }
                handleSearch={searchInputDebounce}
                handleFilter={(e) => console.log(e)}
                handleCreate={() => history.push("/dashboard/receive/new")}
            />

            <div className="levitation-extended mt-[32px]">
                <Can not I="view" a="receive">
                    <NoPermissionBanner />
                </Can>

                <Can I="view" a="receive">
                    <BaseTable
                        data={receiptsResponse?.payload ?? []}
                        columns={[
                            {
                                header: t("table.columns.receiveId"),
                                getter: (row) => (
                                    <>
                                        <div className="bold-highlight">{row.code ?? row.id}</div>
                                    </>
                                ),
                            },
                            {
                                header: t("table.columns.purchaseOrder"),
                                getter: (row) => (
                                    <Link
                                        to={`/dashboard/purchasing/${row.purchase_order.id}/details`}
                                    >
                                        <u>{row.purchase_order.code ?? row.purchase_order.id}</u>
                                    </Link>
                                ),
                            },
                            {
                                header: t("table.columns.supplier"),
                                getter: (row) => row.supplier.name,
                                comparator: () => 0,
                            },
                            {
                                header: t("table.columns.date"),
                                getter: (row) => row.date.format("DD.MM.YYYY"),
                                comparator: () => 0,
                            },
                            {
                                header: t("table.columns.documentNumber"),
                                getter: (row) => row.document_number || "-",
                                visible: showDocumentColumns,
                            },
                            {
                                header: (
                                    <div className="text-center">{t("table.columns.docs")}</div>
                                ),
                                getter: (row) => {
                                    const attachmentsCount = getReceiptAttachmentsCount(row);

                                    if (!attachmentsCount && !row.has_attachments) {
                                        return <div className="text-center">-</div>;
                                    }

                                    return (
                                        <div className="flex items-center justify-center gap-[6px]">
                                            <Paperclip size={16} aria-hidden="true" />
                                            <span>
                                                {attachmentsCount || t("table.attachments.present")}
                                            </span>
                                        </div>
                                    );
                                },
                                visible: showDocumentColumns,
                            },
                            {
                                // visible: ability.can("view", "receive") || ability.can("delete", "receive"),
                                header: (
                                    <div className="text-center">{t("table.columns.action")}</div>
                                ),
                                getter: (row, index, isCollapsed) => (
                                    <div className="text-center space-x-[12px]">
                                        <Can I="view" a="receive">
                                            <DetailsSVG
                                                className={`${
                                                    isCollapsed
                                                        ? "text-tables-highlightedIcon"
                                                        : "text-tables-secondaryIcon"
                                                } hover:text-tables-highlightedIcon cursor-pointer`}
                                                onClick={() => {
                                                    history.push(
                                                        `/dashboard/receive/${row.id}/details`
                                                    );
                                                }}
                                            />
                                        </Can>
                                        {/*<Can I="delete" a="receive">*/}
                                        {/*  <DeleteSVG*/}
                                        {/*    className={`${isCollapsed ? "text-tables-highlightedIcon" : "text-tables-secondaryIcon"} hover:text-tables-highlightedIcon cursor-pointer`}*/}
                                        {/*    onClick={() => setDeletionReceivementId(row.id)} */}
                                        {/*  />*/}
                                        {/*</Can>*/}
                                    </div>
                                ),
                                preventCollapsePropagation: true,
                            },
                        ]}
                        collapse={{
                            fill: "#D9DAFD",
                            borderColor: "#B3B5EB",
                            content: collapsedContent,
                        }}
                        alternate
                        isDataLoading={areReceiptsLoading}
                        manualControls={{
                            ordering: (newOrdering) => {
                                if (newOrdering) {
                                    let name: Receive.DTO.OrderBy | undefined;

                                    switch (newOrdering?.index) {
                                        case 2:
                                            name = "supplier";
                                            break;
                                        case 3:
                                            name = "date";
                                            break;
                                        default:
                                            name = undefined;
                                    }

                                    if (name)
                                        setOrderBy({
                                            name,
                                            type: newOrdering.order,
                                        });
                                } else {
                                    setOrderBy(undefined);
                                }
                            },
                        }}
                    />

                    <BaseTablePaginationControls
                        className="mt-[32px]"
                        pagination={pagination}
                        total={receiptsResponse}
                    />
                </Can>
            </div>

            <DeletionModal
                isOpen={!!deletionReceivementId}
                onClose={() => setDeletionReceivementId(undefined)}
                entity_id={deletionReceivementId}
            />
        </>
    );
}
